import { useState, useEffect, useRef, useCallback } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { acquireApiToken } from '@/auth/acquireApiToken';
import { Env, getAdminApiUrl } from "../shared";

// ─────────────────────────────────────────────────────────────────────────────
// Types — mirror backend OpenAPI for /admin/area-notifications endpoints.
// ─────────────────────────────────────────────────────────────────────────────

export type AreaPreviewRequest =
  | { market_id: string }
  | { center_lat: number; center_lng: number; radius_mi: number };

export interface AreaPreviewResponse {
  audience_count: number;
  tile_count: number;
  center_lat: number;
  center_lng: number;
  radius_mi: number;
  market_name?: string | null;
}

export interface SendAreaBroadcastRequest {
  title: string;
  body: string;
  deep_link_url?: string;
  center_lat: number;
  center_lng: number;
  radius_mi: number;
  market_id?: string;
}

export type AreaBroadcastStatus = "queued" | "completed" | "failed";

export interface SendAreaBroadcastResponse {
  id: string;
  audience_count: number;
  delivered_count: number;
  status: AreaBroadcastStatus;
}

export interface AreaBroadcastRow {
  id: string;
  admin_sub: string;
  title: string;
  body: string;
  deep_link_url: string | null;
  center_lat: number;
  center_lng: number;
  radius_mi: number;
  market_id: string | null;
  audience_count: number;
  delivered_count: number | null;
  status: AreaBroadcastStatus;
  created_at: string;
  completed_at: string | null;
  opened_count: number;
}

interface AreaBroadcastsResponse {
  items: AreaBroadcastRow[];
}

// ─────────────────────────────────────────────────────────────────────────────
// useAreaPreview — POST /admin/area-notifications/preview
// ─────────────────────────────────────────────────────────────────────────────

export interface UseAreaPreviewResult {
  loading: boolean;
  error: string | null;
  preview: (env: Env, req: AreaPreviewRequest) => Promise<AreaPreviewResponse | null>;
}

export function useAreaPreview(): UseAreaPreviewResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useCallback(
    async (env: Env, req: AreaPreviewRequest): Promise<AreaPreviewResponse | null> => {
      const apiUrl = getAdminApiUrl(env);
      if (!apiUrl) {
        setError("API URL is not configured for this environment");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const response = await fetch(`${apiUrl}/admin/area-notifications/preview`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(req),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          setError(data.error || data.message || `HTTP ${response.status}`);
          return null;
        }

        return data as AreaPreviewResponse;
      } catch (err: any) {
        setError(err?.message ?? "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance],
  );

  return { loading, error, preview };
}

// ─────────────────────────────────────────────────────────────────────────────
// useSendAreaBroadcast — POST /admin/area-notifications
// ─────────────────────────────────────────────────────────────────────────────

export interface UseSendAreaBroadcastResult {
  loading: boolean;
  error: string | null;
  send: (
    env: Env,
    req: SendAreaBroadcastRequest,
  ) => Promise<SendAreaBroadcastResponse | null>;
}

export function useSendAreaBroadcast(): UseSendAreaBroadcastResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(
    async (
      env: Env,
      req: SendAreaBroadcastRequest,
    ): Promise<SendAreaBroadcastResponse | null> => {
      const apiUrl = getAdminApiUrl(env);
      if (!apiUrl) {
        setError("API URL is not configured for this environment");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const response = await fetch(`${apiUrl}/admin/area-notifications`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(req),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          setError(data.error || data.message || `HTTP ${response.status}`);
          return null;
        }

        return data as SendAreaBroadcastResponse;
      } catch (err: any) {
        setError(err?.message ?? "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance],
  );

  return { loading, error, send };
}

// ─────────────────────────────────────────────────────────────────────────────
// useAreaBroadcastsList — GET /admin/area-notifications?limit=N
// ─────────────────────────────────────────────────────────────────────────────

export interface UseAreaBroadcastsListOptions {
  env: Env;
  limit?: number;
  // When true, the hook returns the empty default state and skips the fetch.
  // Used by HistoryTable when items are passed in from a parent that already
  // fetched the broadcasts list.
  skip?: boolean;
}

export interface UseAreaBroadcastsListResult {
  items: AreaBroadcastRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAreaBroadcastsList(
  opts: UseAreaBroadcastsListOptions,
): UseAreaBroadcastsListResult {
  const { env, limit = 50, skip = false } = opts;
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [items, setItems] = useState<AreaBroadcastRow[]>([]);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refetch = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchList = async () => {
      const apiUrl = getAdminApiUrl(env);
      if (!apiUrl) {
        if (isMounted) {
          setError("API URL is not configured for this environment");
          setLoading(false);
        }
        return;
      }

      if (isMounted) setLoading(true);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const params = new URLSearchParams({ limit: String(limit) });
        const response = await fetch(
          `${apiUrl}/admin/area-notifications?${params}`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );

        const data: AreaBroadcastsResponse = await response.json();
        if (!response.ok) {
          throw new Error((data as any).error || `HTTP ${response.status}`);
        }

        if (isMounted) {
          setItems(data.items ?? []);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? "Unknown error");
          setItems([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchList();
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, instance, env, limit, refreshTick, skip]);

  return { items, loading, error, refetch };
}
